import { NavLink } from '../shared/types';
import { getLiveDomain } from 'builder/components/SiteUrlDropdown/SiteUrlDropdown.util';

type DraggedNavTreeItem = {
  index?: string | number;
  data?: {
    id?: number;
  };
};

type DragTarget = {
  targetType?: 'item' | 'between-items' | string;
  targetItem?: string | number;
  parentItem?: string | number | null;
  childIndex?: number;
};

type TreeItemNode = {
  children?: number[];
};

export function sortItems<T extends { id: number }>(
  items: T[],
  treeOrder: number[]
) {
  items.sort((a, b) => {
    const positionA = treeOrder.indexOf(a.id);
    const positionB = treeOrder.indexOf(b.id);
    const assignedPositionA =
      positionA === -1 ? Number.MAX_SAFE_INTEGER : positionA;
    const assignedPositionB =
      positionB === -1 ? Number.MAX_SAFE_INTEGER : positionB;
    return assignedPositionA - assignedPositionB;
  });
}

/**
 * Sentinel offset used to build synthetic placeholder item IDs for empty
 * section folders. A placeholder for section N has ID `PLACEHOLDER_OFFSET + N`.
 * These IDs never appear in real nav-link data (real IDs are small integers).
 */
export const PLACEHOLDER_OFFSET = 100_000;

/**
 * Builds the tree-item data object for a synthetic placeholder child.
 * Placeholders are injected into empty section folders so react-complex-tree
 * has a real DOM element for drop-zone hit detection. They are never persisted.
 *
 * @param sectionId - The real ID of the parent section.
 * @returns A tree-item entry keyed by `PLACEHOLDER_OFFSET + sectionId`.
 */
export const buildPlaceholderTreeItem = (sectionId: number) => ({
  index: PLACEHOLDER_OFFSET + sectionId,
  isFolder: false as const,
  children: [] as number[],
  data: {
    isPlaceholder: true,
    id: PLACEHOLDER_OFFSET + sectionId,
    parentSectionId: sectionId,
  },
  canMove: false as const,
});

/**
 * Walks up parentSectionId links from `sectionId` until it reaches a section
 * that is a direct root-level entry in `treeOrder`. Returns that section's id.
 * Used to recover the intended target when react-complex-tree misfires a
 * root-level drop event while the cursor is still visually inside a section.
 */
const findSourceRootSection = (
  sectionId: number,
  treeOrder: number[],
  menuItemById: Map<number, NavLink>
): number | null => {
  const treeOrderSet = new Set(treeOrder);
  let cursor: number | null | undefined = sectionId;
  while (cursor != null && !treeOrderSet.has(cursor)) {
    cursor = menuItemById.get(cursor)?.parentSectionId;
  }
  return cursor ?? null;
};

const warnIfPlaceholderCollision = (menuItems: NavLink[]): void => {
  if (
    process.env.NODE_ENV !== 'production' &&
    menuItems.some(item => item.id >= PLACEHOLDER_OFFSET)
  ) {
    console.warn(
      `resolveDropParentId: a real nav item ID has reached PLACEHOLDER_OFFSET (${PLACEHOLDER_OFFSET}). ` +
        'Increase PLACEHOLDER_OFFSET to prevent ID collisions with synthetic placeholder items.'
    );
  }
};

/**
 * Resolves the true drop-parent ID from a raw react-complex-tree target.
 *
 * Handles two edge cases that the library cannot express cleanly:
 * 1. Drops onto a synthetic placeholder item — strips the PLACEHOLDER_OFFSET
 *    to recover the real parent section ID.
 * 2. Drops that misfire as root-level ('parentItem: root') when the cursor is
 *    near the bottom of a nested empty sub-section — redirects to that
 *    sub-section so the item lands inside it as intended.
 *
 * @param rawParentId - Parent ID decoded from the drop target (null = root).
 * @param target - The full drop target object from react-complex-tree.
 * @param menuItems - Current flat list of all nav items.
 * @param treeOrder - Root-level ordering of top-level item IDs.
 * @param sourceParentId - Parent ID of the dragged item before the drop.
 * @param isDraggedSection - Whether the dragged item is itself a section folder.
 * @returns The resolved numeric parent ID, or null for root-level drops.
 */
export const resolveDropParentId = (
  rawParentId: number | null,
  target: DragTarget,
  menuItems: NavLink[],
  treeOrder: number[],
  sourceParentId?: number | null,
  isDraggedSection?: boolean
): number | null => {
  const menuItemById = new Map(menuItems.map(item => [item.id, item]));
  warnIfPlaceholderCollision(menuItems);

  // Resolve synthetic placeholder IDs back to their real parent section.
  if (rawParentId != null && rawParentId >= PLACEHOLDER_OFFSET) {
    return rawParentId - PLACEHOLDER_OFFSET;
  }

  // React-complex-tree misfires {parentItem:'root'} events when the dragged
  // item is removed from the linear index and the cursor is near the bottom
  // boundary of a nested section (e.g. just below an empty sub-section at the
  // end of its parent). Detect this and redirect to the last empty sub-section
  // of the source's root section — that is almost always the intended target.
  if (
    rawParentId == null &&
    !isDraggedSection &&
    target.targetType === 'between-items' &&
    sourceParentId != null
  ) {
    const rootSectionId = findSourceRootSection(
      sourceParentId,
      treeOrder,
      menuItemById
    );
    if (rootSectionId != null) {
      const rootSection = menuItemById.get(rootSectionId);
      const lastChildId = rootSection?.childOrder?.at(-1);
      if (lastChildId != null) {
        const lastChild = menuItemById.get(lastChildId);
        if (
          lastChild?.isSection &&
          Array.isArray(lastChild.childOrder) &&
          lastChild.childOrder.length === 0
        ) {
          return lastChildId;
        }
      }
    }
  }

  return rawParentId;
};

export const handleDropRequiredItem = (
  items: DraggedNavTreeItem[],
  target: DragTarget,
  menuItems: NavLink[],
  isEditingTemplate: boolean,
  treeOrder: number[] = []
) => {
  if (isEditingTemplate) {
    return true;
  }

  const menuItemById = new Map(menuItems.map(item => [item.id, item]));

  const isRequiredId = (id: number): boolean =>
    menuItemById.get(id)?.required === true;

  const removeIdFromOrder = (order: number[], id: number): number[] =>
    order.filter(orderId => orderId !== id);

  const insertIdIntoOrder = (
    order: number[],
    id: number,
    index: number
  ): number[] => {
    const next = [...order];
    next.splice(index, 0, id);
    return next;
  };

  const hasRequiredPositionChanges = (
    oldOrder: number[],
    newOrder: number[]
  ): boolean => {
    const requiredIds = oldOrder.filter(isRequiredId);
    return requiredIds.some(
      requiredId =>
        oldOrder.indexOf(requiredId) !== newOrder.indexOf(requiredId)
    );
  };

  const getSiblingOrder = (parentSectionId: number | null): number[] => {
    if (parentSectionId == null) {
      return treeOrder.filter(
        id => menuItemById.get(id)?.parentSectionId == null
      );
    }

    const parentItem = menuItemById.get(parentSectionId);
    if (Array.isArray(parentItem?.childOrder) && parentItem.childOrder.length) {
      return parentItem.childOrder.filter(
        id => menuItemById.get(id)?.parentSectionId === parentSectionId
      );
    }

    return menuItems
      .filter(item => item.parentSectionId === parentSectionId)
      .map(item => item.id);
  };

  const draggedItem = menuItems.find(
    menuItem => menuItem.id === (items[0]?.data?.id ?? items[0]?.index)
  );

  if (!draggedItem) return false;
  if (draggedItem.required) return false;

  const sourceParentId = draggedItem.parentSectionId ?? null;

  const toNumericIdOrNull = (value: unknown): number | null =>
    typeof value === 'number' ? value : null;

  let destinationParentId = sourceParentId;
  if (target.targetType === 'item') {
    destinationParentId = toNumericIdOrNull(target.targetItem);
  } else if (target.targetType === 'between-items') {
    destinationParentId = toNumericIdOrNull(target.parentItem);
  }
  destinationParentId = resolveDropParentId(
    destinationParentId,
    target,
    menuItems,
    treeOrder,
    sourceParentId,
    draggedItem.isSection
  );

  const sourceOrder = getSiblingOrder(sourceParentId);
  const sourceOrderAfterRemoval = removeIdFromOrder(
    sourceOrder,
    draggedItem.id
  );

  if (hasRequiredPositionChanges(sourceOrder, sourceOrderAfterRemoval)) {
    return false;
  }

  const destinationOrder =
    sourceParentId === destinationParentId
      ? sourceOrder
      : getSiblingOrder(destinationParentId);

  const destinationBaseOrder =
    sourceParentId === destinationParentId
      ? sourceOrderAfterRemoval
      : destinationOrder;

  const insertionIndex =
    target.targetType === 'between-items' &&
    typeof target.childIndex === 'number'
      ? target.childIndex
      : destinationBaseOrder.length;

  const destinationOrderAfterInsertion = insertIdIntoOrder(
    destinationBaseOrder,
    draggedItem.id,
    insertionIndex
  );

  if (
    hasRequiredPositionChanges(destinationOrder, destinationOrderAfterInsertion)
  ) {
    return false;
  }

  return true;
};

export const getTreeItemChildren = (
  treeItems: Record<string, TreeItemNode>,
  itemId: string | number
) => {
  const item = treeItems?.[itemId];
  if (item && Array.isArray(item.children)) {
    return [...item.children];
  }
  return [];
};

export const generateRoutingSnippet = (
  siteDomain: string,
  pageSlug: string,
  routingId: string,
  parentRoutingId?: string
) => {
  if (!routingId) return '';
  const domain = siteDomain?.trim() ? getLiveDomain(siteDomain) : 'your-domain';
  const slug = pageSlug?.trim() || 'page-slug';
  if (parentRoutingId) {
    return `${domain}/${slug}?tab=${parentRoutingId}&section=${routingId}`;
  }
  return `${domain}/${slug}?section=${routingId}`;
};

export const getTopLevelParentRoutingId = (
  menuItems: NavLink[],
  parentSectionId?: number | null
): string | undefined => {
  if (parentSectionId == null) return undefined;

  let currentParentId: number | null | undefined = parentSectionId;
  let rootParent: NavLink | undefined;
  const visitedIds = new Set<number>();

  while (currentParentId != null && !visitedIds.has(currentParentId)) {
    visitedIds.add(currentParentId);
    const parentItem = menuItems.find(item => item.id === currentParentId);
    if (!parentItem?.isSection) {
      break;
    }

    rootParent = parentItem;
    currentParentId = parentItem.parentSectionId;
  }

  return rootParent?.routingId;
};

export const generateDefaultRoutingId = (displayText: string): string => {
  return displayText
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const getNextNavItemId = (menuItems: NavLink[]): number => {
  const maxId = menuItems.reduce(
    (max, item) => (item.id > max ? item.id : max),
    0
  );
  return maxId + 1;
};

export const removeNavItemWithDescendants = (
  menuItems: NavLink[],
  treeOrder: number[],
  itemId: number | null
): { menuItems: NavLink[]; treeOrder: number[] } => {
  if (itemId == null) {
    return { menuItems, treeOrder };
  }

  const idsToRemove = new Set<number>([itemId]);
  let foundNewDescendant = true;

  while (foundNewDescendant) {
    foundNewDescendant = false;
    menuItems.forEach(item => {
      if (
        item.parentSectionId != null &&
        idsToRemove.has(item.parentSectionId) &&
        !idsToRemove.has(item.id)
      ) {
        idsToRemove.add(item.id);
        foundNewDescendant = true;
      }
    });
  }

  const updatedMenuItems = menuItems
    .filter(item => !idsToRemove.has(item.id))
    .map(item => ({
      ...item,
      childOrder: Array.isArray(item.childOrder)
        ? item.childOrder.filter(childId => !idsToRemove.has(childId))
        : item.childOrder,
    }));

  const updatedTreeOrder = treeOrder.filter(id => !idsToRemove.has(id));

  return {
    menuItems: updatedMenuItems,
    treeOrder: updatedTreeOrder,
  };
};
